import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import io from 'socket.io-client';

function NotificationsDashboard() {
    const [isConnected, setIsConnected] = useState(false);
    const [connectedUsers, setConnectedUsers] = useState(null);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [formData, setFormData] = useState({
        clinicId: '1',
        message: '',
    });

    const API_TOKEN = process.env.REACT_APP_NOTIFICATION_TOKEN || '';
    const NOTIFICATION_SERVER_URL = process.env.REACT_APP_NOTIFICATION_SERVER_URL || 'http://localhost:3000';
    const hasToken = Boolean(API_TOKEN);

    useEffect(() => {
        const socket = io(NOTIFICATION_SERVER_URL);

        socket.on('connect', () => {
            setIsConnected(true);
        });

        socket.on('disconnect', () => {
            setIsConnected(false);
        });

        socket.on('notification', (data) => {
            const message = data.message || JSON.stringify(data);
            toast.info(message, {
                autoClose: 5000,
                position: 'bottom-right',
            });
        });

        return () => {
            socket.close();
            setIsConnected(false);
        };
    }, [NOTIFICATION_SERVER_URL]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const copyServerUrl = () => {
        navigator.clipboard.writeText(NOTIFICATION_SERVER_URL);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!formData.message.trim()) {
            toast.warning('Please enter a message');
            return;
        }

        setIsSending(true);
        try {
            const response = await fetch(`${NOTIFICATION_SERVER_URL}/sendMessage`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${API_TOKEN}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    clinicId: formData.clinicId,
                    message: formData.message,
                }),
            });

            const data = await response.json();
            if (response.ok) {
                toast.success('Message sent successfully!');
                setFormData((prev) => ({ ...prev, message: '' }));
            } else {
                toast.error(`Error: ${data.error || 'Failed to send message'}`);
            }
        } catch (error) {
            toast.error('Failed to send message: ' + error.message);
        } finally {
            setIsSending(false);
        }
    };

    const getConnectedUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const response = await fetch(`${NOTIFICATION_SERVER_URL}/currentUsers`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${API_TOKEN}`,
                },
            });

            const data = await response.json();
            if (response.ok) {
                setConnectedUsers(data);
            } else {
                toast.error(`Error: ${data.error || 'Failed to get users'}`);
            }
        } catch (error) {
            toast.error('Failed to get users: ' + error.message);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const roomEntries = connectedUsers
        ? Object.entries(connectedUsers.roomsBreakdown || {})
        : [];

    return (
        <>
            <div className="d-flex align-items-end justify-content-between gap-2 mb-3 px-2 pt-2">
                <div>
                    <h5 className="notifications-card__title mb-1">Notification Server</h5>
                    <p className="text-muted small mb-0">
                        Monitor connection status, view active users, and send test messages.
                    </p>
                </div>
                <span
                    className={`notifications-status-badge ${
                        isConnected
                            ? 'notifications-status-badge--connected'
                            : 'notifications-status-badge--disconnected'
                    }`}
                >
                    <span className="notifications-status-dot"></span>
                    {isConnected ? 'Connected' : 'Disconnected'}
                </span>
            </div>

            <div className="row g-3 px-2 pb-2">
                <div className="col-lg-6">
                    <div className="notifications-panel">
                        <div className="notifications-panel__header">
                            <i className="bi bi-hdd-network"></i>
                            Server &amp; Users
                        </div>
                        <div className="notifications-panel__body">
                            <div className="notifications-info-row">
                                <span className="notifications-info-label">Server URL</span>
                                <div className="notifications-url-box">
                                    <code>{NOTIFICATION_SERVER_URL}</code>
                                    <button
                                        type="button"
                                        className="notifications-copy-btn"
                                        onClick={copyServerUrl}
                                        title="Copy server URL"
                                    >
                                        <i className={`bi ${copiedUrl ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
                                    </button>
                                </div>
                            </div>

                            <div className="notifications-info-row">
                                <span className="notifications-info-label">API Token</span>
                                <span
                                    className={`notifications-token-badge ${
                                        hasToken
                                            ? 'notifications-token-badge--ok'
                                            : 'notifications-token-badge--missing'
                                    }`}
                                >
                                    <i className={`bi ${hasToken ? 'bi-shield-check' : 'bi-exclamation-triangle'}`}></i>
                                    {hasToken ? 'Configured' : 'Not configured'}
                                </span>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-3">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={getConnectedUsers}
                                    disabled={isLoadingUsers || !isConnected || !hasToken}
                                >
                                    <i
                                        className={`bi bi-arrow-clockwise me-1 ${
                                            isLoadingUsers ? 'notifications-spin' : ''
                                        }`}
                                    ></i>
                                    {isLoadingUsers ? 'Loading…' : 'Refresh Users'}
                                </Button>
                            </div>

                            {connectedUsers ? (
                                <>
                                    <div className="notifications-stat-grid mt-3">
                                        <div className="notifications-stat">
                                            <div className="notifications-stat__value">
                                                {connectedUsers.totalConnectedUsers}
                                            </div>
                                            <div className="notifications-stat__label">Connected Users</div>
                                        </div>
                                        <div className="notifications-stat">
                                            <div className="notifications-stat__value">{roomEntries.length}</div>
                                            <div className="notifications-stat__label">Active Rooms</div>
                                        </div>
                                        <div className="notifications-stat">
                                            <div
                                                className="notifications-stat__value"
                                                style={{ fontSize: '0.85rem' }}
                                            >
                                                {new Date(connectedUsers.timestamp).toLocaleTimeString()}
                                            </div>
                                            <div className="notifications-stat__label">Last Refreshed</div>
                                        </div>
                                    </div>

                                    {roomEntries.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table notifications-rooms-table align-middle">
                                                <thead>
                                                    <tr>
                                                        <th>Room</th>
                                                        <th className="text-end">Users</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {roomEntries.map(([room, count]) => (
                                                        <tr key={room}>
                                                            <td>
                                                                <span className="notifications-room-badge">
                                                                    Room {room}
                                                                </span>
                                                            </td>
                                                            <td className="text-end fw-semibold">{count}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="notifications-empty-state py-3">
                                            <i className="bi bi-people"></i>
                                            No active rooms at the moment.
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="notifications-empty-state mt-3">
                                    <i className="bi bi-people"></i>
                                    Click &ldquo;Refresh Users&rdquo; to load connected user data.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="notifications-panel">
                        <div className="notifications-panel__header">
                            <i className="bi bi-send"></i>
                            Send Test Message
                        </div>
                        <div className="notifications-panel__body">
                            <form onSubmit={handleSendMessage}>
                                <div className="mb-3">
                                    <label htmlFor="clinicId" className="notifications-form-label">
                                        Clinic ID
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        id="clinicId"
                                        name="clinicId"
                                        value={formData.clinicId}
                                        onChange={handleInputChange}
                                        placeholder="Enter clinic ID"
                                        required
                                        disabled={!isConnected || !hasToken}
                                    />
                                    <div className="notifications-form-hint">
                                        Message will be delivered to all users registered to this clinic.
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="message" className="notifications-form-label">
                                        Message
                                    </label>
                                    <textarea
                                        className="form-control form-control-sm"
                                        id="message"
                                        name="message"
                                        rows="4"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        placeholder="Enter your test message…"
                                        required
                                        disabled={!isConnected || !hasToken}
                                    ></textarea>
                                </div>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="sm"
                                    disabled={isSending || !isConnected || !hasToken}
                                >
                                    <i className="bi bi-send-fill me-1"></i>
                                    {isSending ? 'Sending…' : 'Send Message'}
                                </Button>

                                {(!isConnected || !hasToken) && (
                                    <div className="notifications-form-hint mt-2">
                                        {!isConnected && 'Connect to the notification server to send messages. '}
                                        {!hasToken && 'Set REACT_APP_NOTIFICATION_TOKEN in your environment.'}
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <div className="notifications-alert-banner mx-2 mb-2">
                <i className="bi bi-info-circle"></i>
                <span>
                    Incoming notifications are displayed as toast messages in the bottom-right corner while this
                    page is open.
                </span>
            </div>
        </>
    );
}

export default NotificationsDashboard;
